/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { supabase, getProfile, isUserAdmin, getAdminRole } from "@/shared/db";
import type { Database } from "@/shared/db";
import type { User } from "@supabase/supabase-js";
import { errorTracker } from "../utils/errorTracker";
import type { AuthContextType, UserType } from "../shared/types";
import { toast } from "sonner";
import {
  useAuthStore,
  selectSessionStatus,
  selectUser,
  selectProfile,
  selectIsAdmin,
  selectAdminRole,
  selectError,
} from "../stores/authStore";
import {
  withTimeout,
  retryWithBackoff,
  isRetryableError,
  sessionMutex,
  isSessionStale,
} from "../utils/sessionUtils";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

/** Same as useAuth but returns null when outside AuthProvider (e.g. ErrorBoundary fallback) */
export const useOptionalAuth = () => useContext(AuthContext) ?? null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Select only what we need from the store
  const sessionStatus = useAuthStore(selectSessionStatus);
  const user = useAuthStore(selectUser);
  const profile = useAuthStore(selectProfile);
  const isAdmin = useAuthStore(selectIsAdmin);
  const adminRole = useAuthStore(selectAdminRole);
  const error = useAuthStore(selectError);

  // Actions from store
  const setSessionStatus = useAuthStore((state) => state.setSessionStatus);
  const setSession = useAuthStore((state) => state.setSession);
  const setProfile = useAuthStore((state) => state.setProfile);
  const setAdminStatus = useAuthStore((state) => state.setAdminStatus);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setUnauthenticated = useAuthStore((state) => state.setUnauthenticated);
  const setError = useAuthStore((state) => state.setError);
  const clearError = useAuthStore((state) => state.clearError);
  const updateLastSync = useAuthStore((state) => state.updateLastSync);
  const incrementRetryCount = useAuthStore(
    (state) => state.incrementRetryCount,
  );
  const reset = useAuthStore((state) => state.reset);

  /**
   * Load user profile and admin status with timeout and retry
   */
  const loadUserData = useCallback(
    async (authUser: User, isBackground = false): Promise<void> => {
      const userId = authUser.id;

      // Use mutex to prevent concurrent loads
      return sessionMutex.runExclusive(async () => {
        try {
          // Only set loading status if not a background refresh
          if (!isBackground) {
            setSessionStatus("loading_profile");
          }

          // Parallel API calls with timeout and retry
          // Using 20s timeout for profile loads (can be very slow on poor networks)
          const loadWithRetry = async () => {
            const [profileResult, adminStatusResult] = await Promise.allSettled(
              [
                withTimeout(
                  getProfile(userId),
                  20000,
                  "Profile load timed out",
                ),
                withTimeout(
                  isUserAdmin(userId),
                  20000,
                  "Admin check timed out",
                ),
              ],
            );

            return { profileResult, adminStatusResult };
          };

          const { profileResult, adminStatusResult } = await retryWithBackoff(
            loadWithRetry,
            3,
            1000,
            (error) => isRetryableError(error),
          );

          // Handle profile result
          let profileData: Profile | null = null;

          if (profileResult.status === "fulfilled") {
            profileData = profileResult.value as Profile;

            // Check if account is marked for deletion
            if (profileData.deleted_request === true) {
              toast.error(
                "Your account has been marked for deletion. Access has been revoked.",
                { duration: 5000 },
              );
              // Sign out first, then clear local state to avoid race condition
              await supabase.auth.signOut();
              errorTracker.clearUser();
              reset();
              return;
            }
          } else {
            const errorMessage =
              (profileResult.reason as Error)?.message || "Unknown error";
            console.error(`[AuthContext] Profile load failed:`, errorMessage);

            const isNotFound =
              errorMessage.includes("No rows") ||
              errorMessage.includes("PGRST116");

            if (isNotFound) {
              // Fallback profile for new users
              profileData = {
                id: userId,
                full_name: "",
                email: "",
                user_type: null,
                onboarding_completed: false,
                country: null,
                credits_remaining: 0,
                is_active: true,
                deleted_request: false,
                plan: "free",
                profile_image_url: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              } as Profile;
            } else if (profile && profile.id === userId) {
              // If we have cached profile data for this user, use it!
              // This handles slow API responses - we can still function with cached data
              console.warn(
                "[AuthContext] Profile API failed but using cached data:",
                errorMessage,
              );
              profileData = profile;
              // Schedule background refresh to get fresh profile (e.g. updated full_name)
              queueMicrotask(() => loadUserData(authUser, true));
            } else {
              // Critical error - no profile data available at all
              console.error(
                `[AuthContext] Critical: No profile data available and no cache`,
              );
              throw new Error(`Failed to load profile: ${errorMessage}`);
            }
          }

          // Handle admin status result
          let newIsAdmin = false;
          let newAdminRole: string | null = null;

          if (adminStatusResult.status === "fulfilled") {
            newIsAdmin = adminStatusResult.value as boolean;

            if (newIsAdmin) {
              try {
                newAdminRole = await withTimeout(
                  getAdminRole(userId),
                  20000,
                  "Admin role load timed out",
                );
              } catch (error) {
                console.error("[AuthContext] Error loading admin role:", error);
                // Don't fail completely if admin role load fails
                newAdminRole = adminRole || null; // Keep existing role or null
              }
            }
          } else {
            console.error(
              "[AuthContext] Error checking admin status:",
              adminStatusResult.reason,
            );
            // Don't fail completely if admin check fails - use existing values
            newIsAdmin = isAdmin;
            newAdminRole = adminRole;
          }

          // Atomic update - set everything at once
          if (profileData) {
            setAuthenticated(authUser, profileData, newIsAdmin, newAdminRole);

            // Set user context in Sentry
            errorTracker.setUser({
              id: profileData.id,
              email: profileData.email || undefined,
              role: profileData.user_type || undefined,
              name: profileData.full_name || undefined,
            });

            // Identify user in PostHog
            const { analytics } = await import("../utils/analytics");
            analytics.identify(profileData.id, {
              email: profileData.email,
              name: profileData.full_name,
              user_type: profileData.user_type,
              plan: profileData.plan,
              country: profileData.country,
              is_admin: newIsAdmin,
            });
          } else {
            console.error(`[AuthContext] ❌ Profile data is null after load`);
            throw new Error("Profile data is null after load");
          }
        } catch (error) {
          console.error("[AuthContext] ❌ Error in loadUserData:", error);

          const err = error as Error;

          // If we have existing valid profile data, don't show error - just keep using it
          if (profile && profile.id === userId) {
            console.warn(
              "[AuthContext] Load failed but we have valid cached data, continuing with cache",
            );
            // Mark as authenticated with cached data
            setAuthenticated(authUser, profile, isAdmin, adminRole);
            updateLastSync();
            return;
          }

          console.error(`[AuthContext] Setting error state:`, err.message);
          setError(
            err.name === "TimeoutError" ? "timeout" : "profile_load_failed",
            err.message,
            isRetryableError(err),
          );
        }
      });
    },
    [
      profile,
      isAdmin,
      adminRole,
      setSessionStatus,
      setAuthenticated,
      setError,
      reset,
      updateLastSync,
    ],
  );

  /**
   * Initialize authentication
   */
  useEffect(() => {
    let mounted = true;
    let initStarted = false;

    const initAuth = async () => {
      // Prevent multiple initializations
      if (initStarted) {
        return;
      }
      initStarted = true;

      try {
        const {
          sessionStatus: currentStatus,
          rehydrated: currentRehydrated,
          user: currentUser,
          profile: currentProfile,
          lastSuccessfulSync: currentLastSuccessfulSync,
        } = useAuthStore.getState();

        // If we're already authenticated, skip initialization
        if (currentStatus === "authenticated") {
          return;
        }

        // If we have rehydrated data that's fresh, validate it
        if (
          currentRehydrated &&
          currentUser &&
          currentProfile &&
          currentStatus === "hydrating"
        ) {
          // Check if session is stale
          if (!isSessionStale(currentLastSuccessfulSync)) {
            // Data is fresh, verify session is still valid
            const {
              data: { session },
            } = await supabase.auth.getSession();

            if (!mounted) return;

            if (session?.user && session.user.id === currentUser.id) {
              // Session is valid, mark as authenticated
              setSessionStatus("authenticated");
              // Background refresh to get latest profile (e.g. full_name updates)
              queueMicrotask(() => loadUserData(session.user));
              return;
            } else {
              // Session is invalid, clear and re-initialize
              reset();
            }
          }
        }

        // Start fresh initialization
        setSessionStatus("initializing");

        // Check current session
        let {
          data: { session },
        } = await supabase.auth.getSession();

        // If no session is found, try an explicit refresh once (edge cases)
        if (!session) {
          try {
            const { data: refreshData, error: refreshError } =
              await supabase.auth.refreshSession();
            if (refreshError) {
              console.warn(
                "[AuthContext] refreshSession failed:",
                refreshError.message,
              );
            }
            session = refreshData.session ?? null;
          } catch (e) {
            console.warn("[AuthContext] refreshSession threw:", e);
          }
        }

        if (!mounted) return;

        if (session?.user) {
          // We have a session, load user data
          setSession(session.user);
          await loadUserData(session.user);
        } else {
          // No session
          setUnauthenticated();
        }
      } catch (error) {
        console.error("[AuthContext] Error initializing auth:", error);

        if (mounted) {
          const err = error as Error;
          setError(
            err.name === "TimeoutError" ? "timeout" : "unknown",
            err.message,
            isRetryableError(err),
          );
        }
      }
    };

    initAuth();

    // Listen to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_IN" && session?.user) {
        const { sessionStatus: currentStatus } = useAuthStore.getState();

        // Skip if we're already authenticated or loading
        // (happens when initAuth triggers SIGNED_IN during initialization)
        if (currentStatus === "authenticated") {
          return;
        }

        if (currentStatus === "loading_profile") {
          return;
        }

        setSession(session.user);
        await loadUserData(session.user);
      } else if (event === "SIGNED_OUT") {
        setUnauthenticated();
        errorTracker.clearUser();
        // Reset analytics user
        import("../utils/analytics").then(({ analytics }) => {
          analytics.signOut();
        });
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        // Just update session, no need to reload profile
        setSession(session.user);
        updateLastSync();
      } else if (event === "USER_UPDATED" && session?.user) {
        // User data changed, reload profile
        setSession(session.user);
        await loadUserData(session.user);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [
    loadUserData,
    reset,
    setError,
    setSession,
    setSessionStatus,
    setUnauthenticated,
    updateLastSync,
  ]);

  /**
   * Sign up new user
   */
  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      try {
        setSessionStatus("authenticating");

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/verify`,
            data: {
              full_name: fullName,
            },
          },
        });

        if (error) throw error;

        if (data.user) {
          try {
            const profileData = await withTimeout(
              getProfile(data.user.id),
              5000,
            );
            setProfile(profileData);
          } catch {
            // Profile will be created by trigger
          }

          // Track signup
          import("../utils/analytics").then(({ analytics }) => {
            analytics.signUp("email");
          });
        }
      } catch (error) {
        const err = error as Error;
        setError("unknown", err.message, false, err);
        throw error;
      }
    },
    [setSessionStatus, setProfile, setError],
  );

  /**
   * Sign in existing user
   */
  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        setSessionStatus("authenticating");

        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        import("../utils/analytics").then(({ analytics }) => {
          analytics.signIn("email");
        });
      } catch (error) {
        const err = error as Error;
        setError("unknown", err.message, false, err);
        throw error;
      }
    },
    [setSessionStatus, setError],
  );

  /**
   * Reset password
   */
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/update-password`,
    });

    if (error) throw error;
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUnauthenticated();
    errorTracker.clearUser();
  }, [setUnauthenticated]);

  /**
   * Refresh profile data
   */
  const refreshProfile = useCallback(async (): Promise<void> => {
    if (user) {
      await loadUserData(user);
    }
  }, [user, loadUserData]);

  /**
   * Check admin status
   */
  const checkAdminStatus = useCallback(async () => {
    if (user) {
      try {
        const isAdminResult = await isUserAdmin(user.id);
        let role = null;
        if (isAdminResult) {
          role = await getAdminRole(user.id);
        }
        setAdminStatus(isAdminResult, role);
        return isAdminResult;
      } catch (error) {
        console.error("Error checking admin status:", error);
        return false;
      }
    }
    return false;
  }, [user, setAdminStatus]);

  /**
   * Check if user needs onboarding
   */
  const needsOnboarding = useCallback(() => {
    if (!profile) return false;
    if (!profile.user_type) return true;
    if (!profile.onboarding_completed) return true;
    return false;
  }, [profile]);

  /**
   * Check if onboarding is completed
   */
  const isOnboardingCompleted = useCallback(() => {
    if (!profile) return false;
    return !!(profile.user_type && profile.onboarding_completed);
  }, [profile]);

  /**
   * Get user type
   */
  const getUserType = useCallback(() => {
    return (profile?.user_type as UserType) || null;
  }, [profile]);

  /**
   * Retry failed session initialization
   */
  const retrySession = useCallback(async () => {
    if (error && error.retryable) {
      incrementRetryCount();
      clearError();

      // Retry initialization
      try {
        setSessionStatus("initializing");
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          setSession(session.user);
          await loadUserData(session.user);
        } else {
          setUnauthenticated();
        }
      } catch (err) {
        const error = err as Error;
        setError(
          error.name === "TimeoutError" ? "timeout" : "unknown",
          error.message,
          isRetryableError(error),
        );
      }
    }
  }, [
    error,
    incrementRetryCount,
    clearError,
    setSessionStatus,
    setSession,
    loadUserData,
    setUnauthenticated,
    setError,
  ]);

  // Compute loading and initializing from sessionStatus
  const loading =
    sessionStatus === "initializing" ||
    sessionStatus === "hydrating" ||
    sessionStatus === "loading_profile";
  const isInitializing =
    sessionStatus === "idle" ||
    sessionStatus === "initializing" ||
    sessionStatus === "hydrating";

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      loadingProfile: sessionStatus === "loading_profile",
      loadingAdmin: sessionStatus === "loading_profile",
      isInitializing,
      isAdmin,
      adminRole,
      error: error?.message || null,
      sessionStatus,
      signUp,
      signIn,
      resetPassword,
      signOut,
      refreshProfile,
      checkAdminStatus,
      needsOnboarding,
      isOnboardingCompleted,
      getUserType,
      retrySession,
    }),
    [
      user,
      profile,
      loading,
      sessionStatus,
      isInitializing,
      isAdmin,
      adminRole,
      error,
      signUp,
      signIn,
      resetPassword,
      signOut,
      refreshProfile,
      checkAdminStatus,
      needsOnboarding,
      isOnboardingCompleted,
      getUserType,
      retrySession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
